/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { FetchBaseQueryError, QueryDefinition } from "@reduxjs/toolkit/query";
import { generatedApi } from "./generatedApi";

type EndpointEnhancer = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  endpoint: QueryDefinition<any, any, any, any, any>
) => void;

const enableNullBody: EndpointEnhancer = (endpoint) => {
  const query = endpoint.query?.bind(endpoint);
  if (!query) return;
  endpoint.query = (queryArg) => {
    const base = query(queryArg) as Record<string, unknown>;
    return { ...base, body: base.body ?? undefined };
  };
};

const enableExcelToggle: EndpointEnhancer = (endpoint) => {
  const query = endpoint.query?.bind(endpoint);
  if (!query) return;
  endpoint.query = (queryArg) => {
    const base = query(queryArg) as Record<string, unknown>;

    if ((queryArg as Record<string, unknown> | null)?.__excel === true) {
      return {
        ...base,
        headers: {
          ...((base.headers as Record<string, string>) ?? {}),
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        responseHandler: (response: Response) => response,
      };
    }
    return base;
  };
};

const excelCapableEndpoints: (keyof typeof generatedApi.endpoints)[] = [
  "getStrategyDashboardInventory",
  "getStrategyDashboardConsumption",
  "getStrategyDashboardProduction",
  "getStrategyDashboardFailureRate",
  "getStrategyDashboardCost",
  "getStrategyDashboardPrices",
  "getStrategyDashboardHeats",
  "getStrategyDashboardScrapChefUsage",
  "getStrategyDashboardDeliveriesAndPurchases",
  "getStrategyDashboardCompliance",
];

const excelEnhancers = Object.fromEntries(
  excelCapableEndpoints.map((name) => [name, enableExcelToggle])
);

const reviewMutationInvalidatesTags = (
  _result: unknown,
  error: FetchBaseQueryError | undefined
): "label_review"[] =>
  error?.status === 409 || error?.status === 503 ? [] : ["label_review"];

const enhancedApi = generatedApi.enhanceEndpoints({
  endpoints: {
    ...excelEnhancers,
    uploadCorrectedEventsAndVerify: {
      invalidatesTags: reviewMutationInvalidatesTags,
    },
    unverifyPeriod: {
      invalidatesTags: reviewMutationInvalidatesTags,
    },
    getPlanResultsExcel: (endpoint) => {
      const query = endpoint.query?.bind(endpoint);
      if (!query) return;
      endpoint.query = (queryArg) =>
        Object.assign(query(queryArg), {
          responseHandler: (response: Response) => response.blob(),
        });
    },

    sendSearchNotification: (endpoint) => {
      endpoint.invalidatesTags = [];
    },

    listSessions: (endpoint) => {
      endpoint.providesTags = ["session", "search"];
    },
    listSessionVersions: (endpoint) => {
      endpoint.providesTags = ["session", "search"];
    },
    listSearches: (endpoint) => {
      endpoint.providesTags = [{ type: "search", id: "list" }];
    },

    runSearch: (endpoint) => {
      endpoint.invalidatesTags = ["search", "result"];
    },
    createSearch: (endpoint) => {
      endpoint.invalidatesTags = [{ type: "search", id: "list" }];
    },
    getSearch: (endpoint) => {
      endpoint.providesTags = ["search"];
    },
    getSearchContext: (endpoint) => {
      endpoint.providesTags = ["search"];
    },

    getChargeScheduleMessageTemplate: (endpoint) => {
      endpoint.providesTags = [];
    },

    getLayering: (endpoint) => {
      endpoint.invalidatesTags = [];
    },

    validateAutoRecipeDefaults: (endpoint) => {
      endpoint.invalidatesTags = [];
    },
    previewFixedMixDefaults: (endpoint) => {
      endpoint.invalidatesTags = [];
    },
    getFixedMixDefaults: (endpoint) => {
      endpoint.providesTags = ["fixed_mix_defaults", "materials"];
    },
    updateFixedMixDefaults: (endpoint) => {
      endpoint.invalidatesTags = [
        "fixed_mix_defaults",
        "parameter_feasibility",
        "search",
      ];
    },
    previewLayering: (endpoint) => {
      endpoint.invalidatesTags = [];
    },

    listUploadedFileTypes: enableNullBody,

    // Edge-pair hover previews, the bring-in modal and the page read these
    // endpoints over overlapping ranges; hold cached ranges for five minutes so
    // revisiting one within a review session serves from cache instead of refetching.
    getEventsForReview: { keepUnusedDataFor: 300 },
    getLabellerTimelinesReport: { keepUnusedDataFor: 300 },
    getEventLabellingModelAlwaysOnFlags: {
      keepUnusedDataFor: 1800,
      providesTags: [],
    },
  },
});

export { enhancedApi as backendApi };

export async function downloadAsExcel<TArg extends Record<string, unknown>>(
  trigger: (
    arg: TArg | (TArg & { __excel?: boolean })
  ) => Promise<{ data?: unknown }>,
  arg: TArg
): Promise<Response> {
  const response = await trigger({
    ...arg,
    __excel: true,
  });
  // Excel requests preserve the raw response for filename headers and blob conversion.
  return response.data as Response;
}
